import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, AvatarGroup } from '@mui/material';
import SettingsSlider from '../../../settings/SettingsPages/SettingsComponents/SettingsSlider';
import styles from '../calls_queue.module.scss';
import { useLazyGetQueuePanelDataQuery, usePutQueueLoginsMutation, } from '../../../../redux/services/queueApi';
import { QueuePanel } from "../../../../types";
import { useTypedSelector } from "../../../../redux/hooks";
import {
    selectAuthUserByUuid,
    selectCurrentUserId,
    selectCurrentUserSipName,
    selectUserSpecificQueueLogin,
} from "../../../../redux/slices";
import { selectAvailableAgentsInQueue, selectSpecificQueueAlertChecker } from "../../../../redux/slices/queueSlice";
import SettingsInfoIcon from "../../../settings/SettingsPages/SettingsComponents/SettingsInfoIcon";
import LoadingSpinner from '../../../../components/loading/LoadingSpinner';
import { numSecToString } from '..';
import { usePanelContext } from '../panelContext';
import { AlertIcon } from '../../../../components/Icon/AlertIcon';

interface QueuesProps {
    panels?: QueuePanel[];
    dataLoading: boolean;
}

interface Alert {
    alert: boolean;
    threshold: number;
}

interface InfoItem {
    title?: string;
    selectionTitle?: string;
    value: string | number;
    alert?: Alert;
    hidden: boolean;
}

interface waitCardProps {
    longestWait: number;
    alert?: Alert;
}

const InfoCard = ({ item, dataLoading }: { item: InfoItem, dataLoading: boolean }) => {
    const getAlertInfoText = () => {
        if (!item.alert?.alert) return null;

        switch (item.title) {
            case 'Agents Online':
                return `Below ${item.alert.threshold} agents online`;
            case 'Agents Available':
                return `Below ${item.alert.threshold} available agents`;
            case 'Average Wait':
                return `Average wait time over ${item.alert.threshold} seconds`;
            case 'Calls Waiting':
                return `More than ${item.alert.threshold} calls waiting`;
            default:
                return null;
        }
    };

    const alertInfoText = getAlertInfoText();

    return (
        <div className={styles.details_card}>
            <div
                className={`${styles.card_details} ${item.alert?.alert ? styles.alert_border : ''}`}>
                <h4>{item.title}</h4>
                {dataLoading ? (<LoadingSpinner color='mamba' size={24} />) : (<>
                    <p className={item.alert?.alert ? styles.alert_text : ''}>{item.value}</p>
                    {item.alert?.alert && (
                        <div className={`${styles.alert} ${styles.info_card}`}>
                            <SettingsInfoIcon
                                className={styles.alert_icon}
                                customMark={<AlertIcon size={16} />}
                                customDisplay={<p className={styles.alert_info_text}>{alertInfoText}</p>}
                            />
                        </div>
                    )}
                </>)}
            </div>
        </div>
    );
}

const WaitCard: React.FC<waitCardProps> = ({ longestWait, alert }) => {
    const spanRef = useRef<HTMLParagraphElement>(null);
    const startSecondsRef = useRef<number | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const updateDisplay = useCallback(() => {
        if (!spanRef.current) return;

        if (startSecondsRef.current === null) {
            spanRef.current.textContent = '00:00:00';
            return;
        }

        spanRef.current.textContent = numSecToString(startSecondsRef.current);

        startSecondsRef.current += 1;
    }, []);

    useEffect(() => {
        const newSeconds = longestWait || 0;
        const oldSeconds = startSecondsRef.current;

        startSecondsRef.current = newSeconds;
        updateDisplay();

        if (newSeconds === 0) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        } else if (!intervalRef.current || oldSeconds !== newSeconds) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(updateDisplay, 1000);
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        };
    }, [longestWait, updateDisplay]);

    return (
        <div
            className={`${styles.details_card} ${styles.wait_card}`}>
            <div className={`${styles.card_details} ${alert?.alert ? styles.alert_border : ''}`}>
                <h4>Longest In Queue</h4>
                <p ref={spanRef} className={alert ? styles.alert_text : ''}>00:00:00</p>
                {alert?.alert && (
                    <div className={`${styles.alert} ${styles.info_card}`}>
                        <SettingsInfoIcon
                            // customMark={<img alt="alert" src={queueAlert} width={16} />}
                            customMark={<AlertIcon size={16} />}
                            customDisplay={<p className={styles.alert_info_text}>Current call waiting
                                over {alert?.threshold} seconds</p>}
                        />
                    </div>
                )}
            </div>
        </div>
    )
};

const PanelAvatar: React.FC<{ uuid: string }> = memo(({ uuid }) => {
    const user = useTypedSelector(state => selectAuthUserByUuid(state, uuid));

    return (
        <Avatar
            className={styles.avatar_image}
            key={uuid}
            src={user?.avatar_key.length ? `https://storage.googleapis.com/v-profiles/${user.avatar_key}` : undefined}
            alt={user?.nickname || ""}
        />
    )
});

const QueueLoginHandle = ({ panel, allQueues, specificQueue }: {
    panel: QueuePanel,
    allQueues: boolean,
    specificQueue: boolean
}) => {
    const userUuid = useTypedSelector(selectCurrentUserId);

    const [putQueueLogin, { isLoading: isQueueLoginLoading }] = usePutQueueLoginsMutation();
    const [triggerGetQueuePanelData] = useLazyGetQueuePanelDataQuery();

    return (
        <div
            className={styles.toggle}
            onClick={(e) => {
                e.stopPropagation()
            }}
        >
            <SettingsSlider
                title={specificQueue ? 'Logout' : 'Login'}
                checked={specificQueue}
                disabled={!allQueues || isQueueLoginLoading}
                loading={isQueueLoginLoading}
                onChange={async () => {
                    try {
                        await putQueueLogin({
                            voip_user_uuid: userUuid,
                            data: {
                                login: allQueues ? !specificQueue : false,
                                queue_id: panel.queue_uuid
                            }
                        }).unwrap();
                        await triggerGetQueuePanelData({ voip_user_uuid: userUuid });
                    } catch (err) {
                        console.error(err);
                    }
                }}
                className={styles.toggle}
            />
        </div >
    );
};

const QueuePanelItem: React.FC<{
    panel: QueuePanel,
    displayIdx: number,
    dataLoading: boolean,
}> = memo(({ panel, displayIdx, dataLoading }) => {
    const {
        updatePanelState,
    } = usePanelContext();

    const info = useTypedSelector(state => selectSpecificQueueAlertChecker(state, panel.queue_uuid));

    const available = useTypedSelector(state => selectAvailableAgentsInQueue(state, panel.queue_uuid))

    const userUuid = useTypedSelector(selectCurrentUserId);
    const userName = useTypedSelector(selectCurrentUserSipName);

    const loginStatus = useTypedSelector(state => selectUserSpecificQueueLogin(state, panel.queue_uuid, userName, userUuid));

    const [showAll, setShowAll] = React.useState<boolean>(false);

    const panelItems: InfoItem[] = useMemo(() => {
        const stats = info?.stats;

        if (!stats && !dataLoading) {
            return [];
        }

        return [
            {
                title: 'Agents Online',
                selectionTitle: 'AGENT',
                value: info?.agentsOnline ?? 0,
                alert: info?.alerts.agentsOnline,
                hidden: false,
            },
            {
                title: 'Agents Available',
                value: available,
                alert: {
                    alert: available < (info?.alerts.agentsAvailable.threshold ?? 0),
                    threshold: info?.alerts.agentsAvailable.threshold,
                },
                hidden: false,
            },
            {
                title: 'Calls Waiting',
                selectionTitle: 'CALL',
                value: info?.in_queue ?? 0,
                hidden: false,
            },
            {
                title: 'Answered Calls',
                value: stats?.num_answered ?? 0,
                hidden: displayIdx > 3,
            },
            {
                title: 'Unanswered Calls',
                value: stats ? stats.num_calls - stats.num_ringing - stats.num_answered : 0,
                hidden: displayIdx > 0,
            },
            {
                title: 'Total Calls',
                value: stats?.num_calls ?? 0,
                hidden: displayIdx > 1,
            },
            {
                title: 'Average Wait',
                value: numSecToString(stats ? stats.average_wait_duration + stats.average_ring_duration : 0),
                alert: info?.alerts.averageWait,
                hidden: displayIdx > 2,
            },
            {
                title: 'Longest In Queue',
                value: info?.in_queue_longest ?? 0,
                alert: info?.alerts.longestWait,
                hidden: false,
            },
        ] as InfoItem[];
    }, [panel, info, available, displayIdx]);

    const isSupervisor = panel.is_supervisor && !panel.queue_users.some(user => user === userUuid);

    return (
        <div
            className={styles.queues_container}
            style={
                !isSupervisor
                    ? {
                        opacity: (loginStatus?.allQueues && loginStatus?.specificQueue) ? 1 : 0.5,
                        cursor: (loginStatus?.allQueues && loginStatus?.specificQueue) ? 'pointer' : 'default',
                    } : { cursor: 'pointer', }
            }>
            <div
                className={styles.queue_details}
                onClick={() => {
                    updatePanelState({
                        selectedTab: 'agents',
                        selectedPanelUuid: panel.uuid,
                    });
                }}
            >
                <div className={styles.details_heading}>
                    <div className={styles.avatar_heading}>
                        <AvatarGroup spacing={8}>
                            {panel.queue_users.slice(0, 2).map(avatarUser => (
                                <PanelAvatar
                                    uuid={avatarUser}
                                    key={avatarUser}
                                />
                            ))}
                        </AvatarGroup>
                        <h4>{panel.name}</h4>
                    </div>
                    {(!isSupervisor && loginStatus) ? (
                        <QueueLoginHandle panel={panel} allQueues={loginStatus.allQueues}
                            specificQueue={loginStatus.specificQueue} />
                    ) : null}
                    {isSupervisor ? (
                        <div className={styles.toggle}>
                            <p className={styles.supervisorTag}>Supervisor</p>
                        </div>
                    ) : null}
                </div>
                <div className={styles.queue_details_container}>
                    {panelItems.map((item, i) => {
                        if (item.hidden && !showAll) {
                            return null
                        }
                        return (
                            <div key={i}>
                                {item.selectionTitle && <h4>{item.selectionTitle}</h4>}
                                {item.title === 'Longest In Queue' ? (
                                    <WaitCard
                                        longestWait={Number.isNaN(item.value) ? 0 : item.value as number}
                                        alert={item.alert}
                                    />
                                ) : (
                                    <InfoCard item={item} dataLoading={dataLoading} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            {
                displayIdx > 0 && (
                    <div className={styles.show_more_container}>
                        <button
                            className={`${styles.show_more_btn} ${info?.alerts?.averageWait.alert ? styles.alert : ''}`}
                            onClick={() =>
                                setShowAll(!showAll)
                            }
                        >
                            {showAll ? 'Show Fewer Insights' : 'Show All Insights'}
                            <i className={`${showAll ? "icon-arrow-up" : "icon-arrow-down"} ${info?.alerts?.averageWait.alert ? styles.alert : ""
                                }`} />
                        </button>
                    </div>
                )
            }
        </div >
    );
});

const Queues = ({ panels, dataLoading }: QueuesProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [displayIdx, setDisplayIdx] = useState(8);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }

        const obs = new ResizeObserver(entries => {
            const containerWidth = entries[0].contentRect.width;

            let newDisplayIdx = 4;

            if (containerWidth >= 845) newDisplayIdx = 0;
            else if (containerWidth >= 746) newDisplayIdx = 1;
            else if (containerWidth >= 647) newDisplayIdx = 2;
            else if (containerWidth >= 617) newDisplayIdx = 3;

            setDisplayIdx(newDisplayIdx)
        });

        obs.observe(containerRef.current);

        return () => {
            obs.disconnect();
        };
    }, []);

    if (!panels || panels.length === 0) return null;

    return (
        <div ref={containerRef}>
            {panels?.map((panel) => (
                <QueuePanelItem
                    panel={panel}
                    key={panel.uuid}
                    displayIdx={displayIdx}
                    dataLoading={dataLoading}
                />
            ))}
        </div>
    );
};

export default Queues;
